import { useState } from 'react';
import swal from 'sweetalert';
import {
  applyPumpAccountingFix,
  friendlyFetchError,
  previewPumpAccountingFix,
} from './adminApi';
import { promptAccountingReason, showAccountingAlert, showAccountingApplyFailure } from './accountingSwal';
import {
  accountingPreviewAlertClass,
  accountingPreviewDetailLine,
  shouldBlockRepairScriptRun,
} from './accountingPreviewUtils';
import { buildAccountingApplyParams } from './accountingApplyParams';
import AccountingPreviewTable from './AccountingPreviewTable';

const RISK_CLASS = { low: 'info', medium: 'warning', high: 'critical' };
const RISK_LABELS = { low: 'Low risk', medium: 'Medium risk', high: 'High risk — preview first' };

export default function AccountingRepairScriptsPanel({
  pumpId,
  fromDate,
  toDate,
  scripts = [],
  intro = null,
  disabled = false,
  onPreviewStart,
  onPreviewEnd,
  onApplyStart,
  onApplyEnd,
  onCompleted,
}) {
  const [previews, setPreviews] = useState({});
  const [previewLoading, setPreviewLoading] = useState(null);
  const [runningScript, setRunningScript] = useState(null);

  if (!scripts.length) {
    return null;
  }

  function validateDates(script) {
    if (script?.uses_date_range === false) {
      return true;
    }
    if (!fromDate || !toDate) {
      showAccountingAlert('Dates required', 'Set From date and To date above before running a repair script.', 'warning');
      return false;
    }
    if (fromDate > toDate) {
      showAccountingAlert('Invalid dates', 'From date must be on or before To date.', 'warning');
      return false;
    }
    return true;
  }

  function runPreview(script) {
    if (!validateDates(script)) return;
    setPreviewLoading(script.fix_type);
    onPreviewStart?.();
    previewPumpAccountingFix(pumpId, script.fix_type, {
      from_date: fromDate,
      to_date: toDate,
    })
      .then((res) => {
        setPreviews((prev) => ({ ...prev, [script.fix_type]: res }));
        if (!res.status) {
          showAccountingAlert('Preview failed', res.message || 'Unable to preview script', 'error');
        }
      })
      .catch((err) => showAccountingAlert('Preview failed', friendlyFetchError(err), 'error'))
      .finally(() => {
        setPreviewLoading(null);
        onPreviewEnd?.();
      });
  }

  async function executeScript(script, reason, previewData) {
    setRunningScript(script.fix_type);
    onApplyStart?.(script.plain_label || script.label);
    try {
      const res = await applyPumpAccountingFix(
        pumpId,
        script.fix_type,
        buildAccountingApplyParams(
          { from_date: fromDate, to_date: toDate },
          previewData
        ),
        reason,
        'bs_pl_mismatch'
      );
      onApplyEnd?.();

      if (res.status) {
        const verification = res.verification || res.apply_result?.verification;
        const msg = verification?.plain
          ? `${verification.plain.headline}\n\n${verification.plain.detail}`
          : (res.message || 'Repair script completed.');
        await showAccountingAlert(
          'Script completed',
          msg,
          verification?.verified_ok ? 'success' : 'warning'
        );
        onCompleted?.(res);
      } else {
        await showAccountingApplyFailure(pumpId, res, 'Script failed');
      }
    } catch (err) {
      onApplyEnd?.();
      await showAccountingAlert(
        'Script interrupted or failed',
        `${friendlyFetchError(err)}\n\nCheck Fix history before running again.`,
        'error'
      );
    } finally {
      setRunningScript(null);
    }
  }

  async function runScript(script) {
    if (!validateDates(script)) return;

    let previewData = previews[script.fix_type];

    async function confirmAndRun(confirmedPreview) {
      const effectivePreview = confirmedPreview ?? previewData;

      if (shouldBlockRepairScriptRun(effectivePreview, script.fix_type)) {
        await showAccountingAlert(
          effectivePreview?.no_sales_in_period ? 'No gas sales in period' : 'Nothing to repair',
          effectivePreview?.message || 'No changes are needed for this script in the selected period.',
          'info'
        );
        return;
      }

      const rowCount = effectivePreview?.rows_to_change ?? 0;
      const rowHint = rowCount > 0
        ? `\n\nAbout ${rowCount} row${rowCount === 1 ? '' : 's'} will be updated.`
        : '';
      const periodHint = script.uses_date_range === false
        ? '\n\nThis checks the whole pump. The selected dates are not used.'
        : `\n\nPeriod: ${fromDate} to ${toDate}`;

      const ok = await swal({
        title: 'Run this repair script?',
        text: `${script.plain_label || script.label}${periodHint}${rowHint}\n\nThis updates ledger rows and saves a Fix history entry.`,
        icon: 'warning',
        buttons: true,
        dangerMode: script.risk === 'high',
      });
      if (!ok) return;

      const reason = await promptAccountingReason(
        'Reason for this repair',
        'Min 10 characters — e.g. BS/PL mismatch from shop GST posting'
      );
      if (reason === null) return;
      if (reason.length < 10) {
        await showAccountingAlert('Reason required', 'Please enter at least 10 characters.', 'warning');
        return;
      }

      await executeScript(script, reason, effectivePreview);
    }

    if (previewData?.status) {
      await confirmAndRun(previewData);
      return;
    }

    setPreviewLoading(script.fix_type);
    onPreviewStart?.();
    try {
      const res = await previewPumpAccountingFix(pumpId, script.fix_type, {
        from_date: fromDate,
        to_date: toDate,
      });
      setPreviews((prev) => ({ ...prev, [script.fix_type]: res }));
      previewData = res;
      if (!res.status) {
        await showAccountingAlert('Preview failed', res.message || 'Unable to preview script', 'error');
        return;
      }
      await confirmAndRun(res);
    } catch (err) {
      await showAccountingAlert('Preview failed', friendlyFetchError(err), 'error');
    } finally {
      setPreviewLoading(null);
      onPreviewEnd?.();
    }
  }

  return (
    <div className="admin-panel accounting-repair-scripts mt-3">
      <h5 className="mb-1">{intro?.title || 'Repair scripts (by date range)'}</h5>
      <p className="small text-muted mb-3">
        {intro?.body || 'Uses the From / To dates from Check my accounts above.'}
        {' '}
        {fromDate && toDate ? (
          <strong>Selected: {fromDate} to {toDate}</strong>
        ) : (
          <span className="text-warning">Set dates above first.</span>
        )}
      </p>

      <div className="accounting-repair-script-list">
        {scripts.map((script) => {
          const preview = previews[script.fix_type];
          const isLoading = previewLoading === script.fix_type;
          const isRunning = runningScript === script.fix_type;

          return (
            <div key={script.script_id || script.fix_type} className="accounting-repair-script-card">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                <div>
                  <strong>{script.plain_label || script.label}</strong>
                  {script.risk && (
                    <span className={`admin-badge audit-severity ${RISK_CLASS[script.risk] || 'info'} ms-2`}>
                      {RISK_LABELS[script.risk] || script.risk}
                    </span>
                  )}
                  <p className="small text-muted mb-1 mt-1">
                    {script.plain_description || script.description}
                  </p>
                  {script.when_to_use && (
                    <p className="small mb-0"><em>When:</em> {script.when_to_use}</p>
                  )}
                  {script.uses_date_range === false && (
                    <p className="small mb-0">Whole pump. Selected dates are not used.</p>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={disabled || isLoading || isRunning}
                    onClick={() => runPreview(script)}
                  >
                    {isLoading ? 'Preview…' : 'Preview'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    disabled={disabled || isLoading || isRunning}
                    onClick={() => runScript(script)}
                  >
                    {isRunning ? 'Running…' : 'Run script'}
                  </button>
                </div>
              </div>

              {preview?.status && (
                <div className={`admin-alert-item mb-0 ${accountingPreviewAlertClass(preview)}`}>
                  <strong>{preview.message}</strong>
                  {accountingPreviewDetailLine(preview) && (
                    <div className="small">{accountingPreviewDetailLine(preview)}</div>
                  )}
                </div>
              )}

              {preview?.status && (
                <div className="mt-2">
                  <AccountingPreviewTable preview={preview} />
                </div>
              )}

              {script.cli_hint && (
                <details className="mt-2">
                  <summary className="small text-muted">Server command (optional)</summary>
                  <code className="small d-block mt-1 accounting-cli-hint">
                    {script.cli_hint
                      .replace('PUMP', pumpId)
                      .replace('YYYY-MM-DD', fromDate || 'YYYY-MM-DD')}
                  </code>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
